import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { useEffect, useRef } from 'react';

interface EmailEditorProps {
  renderedHtml: string;
  renderedSubject: string;
  onHtmlChange: (html: string) => void;
  onSubjectChange: (subject: string) => void;
  isLoading?: boolean;
}

/**
 * Extract the editable inner content from the full rendered email HTML.
 * The email wrapper uses: <div style="font-size: 15px; line-height: 1.7; color: #1a1a1a;">
 * We extract that div's innerHTML and store everything else as the wrapper.
 */
function extractEditableContent(fullHtml: string): {
  before: string;
  content: string;
  after: string;
} {
  const marker = 'font-size: 15px; line-height: 1.7; color: #1a1a1a;';
  const idx = fullHtml.indexOf(marker);

  if (idx === -1) {
    // Fallback: treat entire HTML as editable
    return { before: '', content: fullHtml, after: '' };
  }

  // Find the closing > of the div tag containing the marker
  const openTagEnd = fullHtml.indexOf('>', idx);
  if (openTagEnd === -1) {
    return { before: '', content: fullHtml, after: '' };
  }

  const contentStart = openTagEnd + 1;

  // Find the matching closing </div> — it's the first </div> after content start
  // that precedes the footer (hr tag)
  const footerIdx = fullHtml.indexOf('<hr', contentStart);
  let contentEnd: number;

  if (footerIdx !== -1) {
    // Content ends before the footer/hr
    // But we need to find the content boundary: look for the last content before hr
    contentEnd = footerIdx;
  } else {
    // Find the closing </div> for this content div
    const closingDiv = fullHtml.indexOf('</div>', contentStart);
    contentEnd = closingDiv !== -1 ? closingDiv : fullHtml.length;
  }

  const before = fullHtml.substring(0, contentStart);
  const content = fullHtml.substring(contentStart, contentEnd).trim();
  const after = fullHtml.substring(contentEnd);

  return { before, content, after };
}

function reassembleHtml(before: string, editedContent: string, after: string): string {
  return before + '\n' + editedContent + '\n' + after;
}

export function EmailEditor({
  renderedHtml,
  renderedSubject,
  onHtmlChange,
  onSubjectChange,
  isLoading,
}: EmailEditorProps) {
  const wrapperRef = useRef<{ before: string; after: string }>({ before: '', after: '' });
  const initializedRef = useRef(false);

  // Extract initial content synchronously so useEditor can initialize with it.
  // This avoids a Safari race condition where editor.commands.setContent() in a
  // useEffect fires before the ProseMirror view is fully attached.
  const initialContentRef = useRef('');
  if (!initializedRef.current && renderedHtml) {
    const { before, content, after } = extractEditableContent(renderedHtml);
    wrapperRef.current = { before, after };
    initialContentRef.current = content;
    initializedRef.current = true;
  }

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: {
          HTMLAttributes: {
            style: 'margin: 0 0 20px 0;',
          },
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          style: 'color: #0d9488; text-decoration: none;',
        },
      }),
    ],
    content: initialContentRef.current,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] px-4 py-3',
        style: 'font-size: 15px; line-height: 1.7; color: #1a1a1a; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const editedContent = ed.getHTML();
      const fullHtml = reassembleHtml(
        wrapperRef.current.before,
        editedContent,
        wrapperRef.current.after,
      );
      onHtmlChange(fullHtml);
    },
  });

  // Fallback: if renderedHtml arrives after the editor was already created
  // (e.g. the editor is mounted while the preview is still loading). The
  // render-phase block above marks initializedRef as soon as renderedHtml is
  // set, but the TipTap editor was created earlier with empty content — so we
  // must key off the editor's actual state, not initializedRef (@BUG-127).
  useEffect(() => {
    if (renderedHtml && editor && editor.isEmpty) {
      const { before, content, after } = extractEditableContent(renderedHtml);
      if (!content) return;
      wrapperRef.current = { before, after };
      editor.commands.setContent(content);
      initializedRef.current = true;
    }
  }, [renderedHtml, editor]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3 text-gray-500">
          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="text-sm">E-Mail wird geladen...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Subject input */}
      <div>
        <label htmlFor="email-subject" className="block text-sm font-medium text-gray-700 mb-1">
          Betreff
        </label>
        <input
          id="email-subject"
          type="text"
          value={renderedSubject}
          onChange={(e) => onSubjectChange(e.target.value)}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
        />
      </div>

      {/* Editor */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          E-Mail-Text
        </label>
        <div className="border border-gray-300 rounded-md overflow-hidden">
          {/* Toolbar */}
          <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-200">
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleBold().run()}
              isActive={editor?.isActive('bold')}
              title="Fett"
            >
              <strong>B</strong>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              isActive={editor?.isActive('italic')}
              title="Kursiv"
            >
              <em>I</em>
            </ToolbarButton>
            <div className="w-px h-5 bg-gray-300 mx-1" />
            <ToolbarButton
              onClick={() => editor?.chain().focus().undo().run()}
              disabled={!editor?.can().undo()}
              title="Rückgängig"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a4 4 0 014 4v0a4 4 0 01-4 4H3" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 6l-4 4 4 4" />
              </svg>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor?.chain().focus().redo().run()}
              disabled={!editor?.can().redo()}
              title="Wiederholen"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a4 4 0 00-4 4v0a4 4 0 004 4h10" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 6l4 4-4 4" />
              </svg>
            </ToolbarButton>
          </div>

          {/* Editor content */}
          <EditorContent editor={editor} />
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Kopfzeile und Fußzeile der E-Mail bleiben unverändert.
        </p>
      </div>
    </div>
  );
}

function ToolbarButton({
  children,
  onClick,
  isActive,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded text-sm leading-none ${
        isActive
          ? 'bg-gray-200 text-gray-900'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {children}
    </button>
  );
}
